<script lang="ts" setup>
import { computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { PipelinesDocument, PipelineType } from '@/gql/graphql';
import { BFormGroup, BFormSelect } from 'bootstrap-vue-next';
import { deepParseDates } from '@/utils/date-utils';

// --- Component properties ---
const props = defineProps<{
  label: string | null | undefined;
  enabled: boolean;
  type: string | null | undefined;
  premium: boolean | null | undefined;
  activeOnly: boolean | null | undefined;
  allowNullPipeline: boolean;
  modelValue: PipelineType | null;
}>();

const emit = defineEmits(['update:modelValue']);

// --- Query Pipelines ---
const { result: resultFetchData, onResult: onFetchDataResult } = useQuery(
  PipelinesDocument,
  {
    premium: props.premium ?? undefined,
    type: props.type ?? undefined,
    activeOnly: props.activeOnly ?? undefined,
  }
);

onFetchDataResult((res) => {
  console.debug('Pipelines fetched:', res.data);

  if (props.modelValue) {
    const matching = options.value.find(
      (opt) =>
        opt.value && props.modelValue && opt.value.id === props.modelValue.id
    );
    if (matching) {
      selectedPipeline.value = matching.value;
    }
  } else if (options.value.length > 0 && !props.allowNullPipeline) {
    // If no default value provided, select the first option if null not allowed
    selectedPipeline.value = options.value[0].value;
  }
});

const pipelines = computed(() => {
  const pipes = resultFetchData.value?.pipelines || [];
  return pipes
    .filter((pipe) => pipe && pipe !== null)
    .map((pipe) => wellTypedPipeline(pipe));
});

function wellTypedPipeline(pipe: PipelineType | null): PipelineType {
  return {
    ...(deepParseDates(pipe) as PipelineType),
  };
}

// Helper method to build the display text for the pipeline option
function buildPipelineText(pipe: PipelineType): string {
  let additionalTextItems: string[] = [];
  // version
  if (pipe.version) {
    additionalTextItems.push(`v${pipe.version}`);
  }
  // premium pipelines get a "premium" tag, and if version is present, add it as well
  if (pipe.isPremium) {
    additionalTextItems.push('premium');
  }
  // concatenate additional text items if present
  const additionalText =
    additionalTextItems.length > 0
      ? ` (${additionalTextItems.join(', ')})`
      : '';
  // return the name with additional text if applicable
  return `${pipe.friendlyName}${additionalText}`;
}

// Build the options. If defaultValue is provided and not present,
// add it to the beginning of the list.
const options = computed(() => {
  let pipelineOptions: { text: string; value: PipelineType | null }[] =
    pipelines.value.map((pipeline) => ({
      text: buildPipelineText(pipeline),
      value: pipeline,
    }));
  if (props.modelValue) {
    const defaultExists = pipelineOptions.some(
      (opt) =>
        opt.value && props.modelValue && opt.value.id === props.modelValue.id
    );
    if (!defaultExists) {
      // Prepend the default organization option if not present.
      console.debug('Default organization not found in options, adding it.');
      pipelineOptions.unshift({
        text: buildPipelineText(props.modelValue),
        value: wellTypedPipeline(props.modelValue),
      });
    } else console.debug('Default organization already exists in options');
  }
  // Add a null option at the beginning if requested.
  if (props.allowNullPipeline) {
    pipelineOptions.unshift({ text: 'None', value: null });
  }
  return pipelineOptions;
});

// Use a computed getter/setter for the v-model binding.
const selectedPipeline = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  },
});
</script>
<template>
  <BFormGroup label-for="pipeline-picker-select">
    <template #label v-if="props.label">
      {{ props.label }}
    </template>
    <BFormSelect
      id="pipeline-picker-select"
      name="pipeline-picker-select"
      v-model="selectedPipeline"
      :options="options"
      :disabled="!props.enabled || options.length === 0"
      required
    />
  </BFormGroup>
</template>
